import { useCallback } from 'react'
import { useProgress } from '@/contexts/ProgressProvider'
import { LEVELS, type LevelMeta } from '@/game/levels'
import type { ResolvedRun } from './useLevelRun'
import { computeGraceScore } from '@/game/scoring'
import { evaluateAchievements } from '@/game/achievements'
import { BIOME_ORDER, type BiomeId } from '@/styles/theme'

function biomeGate(biome: BiomeId): number {
  const first = LEVELS.find((l) => l.biome === biome)
  return first?.minStars ?? 0
}

export function useCommitRun() {
  const { update } = useProgress()

  return useCallback(
    (level: LevelMeta, run: ResolvedRun) => {
      if (!run.reachedHome) return
      update((s) => {
        const prevBest = s.progress.levelBest[level.index]
        const prevStars = s.progress.levelStars[level.index] ?? 0
        const bestScore = Math.max(prevBest?.score ?? 0, run.score)
        const bestSlides = Math.min(prevBest?.slides ?? Infinity, run.slides)
        const bestCoins = Math.max(prevBest?.coins ?? 0, run.coinsCollected)
        const bestTime = Math.min(prevBest?.timeMs ?? Infinity, run.timeMs)
        const stars = Math.max(prevStars, run.stars) as 0 | 1 | 2 | 3

        const completedLevels = s.progress.completedLevels.includes(level.index)
          ? s.progress.completedLevels
          : [...s.progress.completedLevels, level.index]

        const levelStars = { ...s.progress.levelStars, [level.index]: stars }
        const levelBest = {
          ...s.progress.levelBest,
          [level.index]: {
            slides: Number.isFinite(bestSlides) ? bestSlides : run.slides,
            coins: bestCoins,
            timeMs: Number.isFinite(bestTime) ? bestTime : run.timeMs,
            score: bestScore,
          },
        }

        const totalStars = Object.values(levelStars).reduce((a: number, b) => a + b, 0)
        const scoreDelta = Math.max(0, run.score - (prevBest?.score ?? 0))
        const totalScore = s.meta.totalScore + scoreDelta

        const unlockedBiomes = [...s.progress.unlockedBiomes]
        for (const biome of BIOME_ORDER) {
          if (!unlockedBiomes.includes(biome) && totalStars >= biomeGate(biome)) {
            unlockedBiomes.push(biome)
          }
        }

        const now = Date.now()
        const next = {
          ...s,
          progress: { ...s.progress, completedLevels, levelStars, levelBest, unlockedBiomes, currentBiome: level.biome },
          meta: { ...s.meta, totalStars, totalScore, home: { lastReturnedHomeAt: now } },
        }
        const newlyEarned = evaluateAchievements(next)
        if (newlyEarned.length > 0) {
          next.meta = { ...next.meta, achievements: [...next.meta.achievements, ...newlyEarned] }
        }
        next.meta.graceScore = computeGraceScore(next)
        return next
      })
    },
    [update],
  )
}
